import { useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '@/components/ui/toast';

import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { PageHeader } from '@/components/shared/page-header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { ApiError } from '@/lib/api-errors';
import { api } from '@/lib/api';
import {
  buildInvoicePdfBlob,
  type BillingDocumentPdfLabels,
  type BillingInvoiceDocument,
} from '@/utils/billing-document-pdf';
import { formatDate, formatMoney } from '@/utils/format';
import { openBlobInNewTab } from '@/utils/open-blob-in-new-tab';

interface InvoiceRow {
  id: string;
  churchId: string;
  churchName?: string;
  invoiceNumber: number;
  status: string;
  plan: string;
  totalMinor: number;
  discountMinor: number;
  currency: string;
  issuedAt: string;
  paidAt: string | null;
}

type InvoiceFilter = 'all' | 'open' | 'paid';

const FILTERS: InvoiceFilter[] = ['all', 'open', 'paid'];

export const InvoicesPage = () => {
  const { t } = useTranslation('admin');
  const { t: tErrors } = useTranslation('errors');
  const [status, setStatus] = useState<InvoiceFilter>('all');
  const [openingDocumentId, setOpeningDocumentId] = useState<string | null>(null);

  const pdfLabels = useCallback(
    (document: BillingInvoiceDocument): BillingDocumentPdfLabels => ({
      merchantName: t('billing.pdf.merchantName'),
      merchantDescriptor: t('billing.pdf.merchantDescriptor'),
      merchantCompany: t('billing.pdf.merchantCompany'),
      merchantContact: t('billing.pdf.merchantContact'),
      merchantAddress: t('billing.pdf.merchantAddress'),
      invoiceTitle: t('billing.pdf.invoiceTitle'),
      invoiceNumber: t('billing.pdf.invoiceNumber', { number: document.invoiceNumber }),
      billTo: t('billing.pdf.billTo'),
      issued: t('billing.pdf.issued'),
      due: t('billing.pdf.due'),
      paid: t('billing.pdf.paid'),
      status: t('billing.pdf.status'),
      period: t('billing.pdf.period'),
      description: t('billing.pdf.description'),
      seats: t('billing.pdf.seats'),
      amount: t('billing.pdf.amount'),
      planLine: t('billing.pdf.planLine', {
        plan: t(`billing.plans.${document.plan as 'essential'}`),
        interval: t(`billing.pdf.intervals.${document.interval as 'annual'}`),
      }),
      seatsLine: t('billing.pdf.seatsLine', { count: document.seatCount }),
      coupon: document.couponCode ? t('billing.pdf.coupon', { code: document.couponCode }) : '',
      subtotal: t('billing.pdf.subtotal'),
      discount: t('billing.pdf.discount'),
      tax: t('billing.pdf.tax'),
      total: t('billing.pdf.total'),
      amountPaid: t('billing.pdf.amountPaid'),
      amountDue: t('billing.pdf.amountDue'),
      receiptTitle: t('billing.pdf.receiptTitle'),
      receiptNumber: t('billing.pdf.receiptNumber', { number: 0 }),
      receivedFrom: t('billing.pdf.receivedFrom'),
      invoiceLabel: t('billing.pdf.invoiceLabel'),
      invoiceRef: t('billing.pdf.invoiceRef', { number: document.invoiceNumber }),
      amountPaidLabel: t('billing.pdf.amountPaid'),
      subscriptionPayment: t('billing.pdf.subscriptionPayment'),
      thankYou: t('billing.pdf.thankYou'),
      statuses: {
        open: t('billing.invoices.statuses.open'),
        paid: t('billing.invoices.statuses.paid'),
        void: t('billing.invoices.statuses.void'),
        uncollectible: t('billing.invoices.statuses.uncollectible'),
      },
    }),
    [t],
  );

  const openInvoicePdf = useCallback(
    async (row: InvoiceRow) => {
      if (openingDocumentId) return;
      setOpeningDocumentId(row.id);
      try {
        const document = await api.get<BillingInvoiceDocument>(`/platform/billing/invoices/${row.id}`);
        const blob = await buildInvoicePdfBlob(
          document,
          pdfLabels(document),
          (amountMinor, currency, locale) => formatMoney(amountMinor, currency, { locale }),
        );
        if (!openBlobInNewTab(blob)) {
          toast.error(t('billing.invoices.popupBlocked'));
        }
      } catch (err) {
        toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
      } finally {
        setOpeningDocumentId(null);
      }
    },
    [openingDocumentId, pdfLabels, t, tErrors],
  );

  const invoices = useQuery({
    queryKey: ['platform-invoices', status],
    queryFn: () =>
      api.get<{ data: InvoiceRow[] }>('/platform/billing/invoices', {
        query: {
          page: 1,
          pageSize: 100,
          ...(status === 'all' ? {} : { status }),
        },
      }),
  });

  const columns: DataTableColumn<InvoiceRow>[] = [
    {
      id: 'issuedAt',
      header: t('billing.invoices.issuedAt'),
      cell: (row) => formatDate(row.issuedAt),
    },
    {
      id: 'invoiceNumber',
      header: t('billing.invoices.invoiceNumber'),
      cell: (row) => `#${row.invoiceNumber}`,
    },
    {
      id: 'church',
      header: t('billing.invoices.church'),
      cell: (row) => row.churchName ?? row.churchId,
    },
    {
      id: 'plan',
      header: t('billing.invoices.plan'),
      cell: (row) => t(`billing.plans.${row.plan as 'essential'}`),
    },
    {
      id: 'total',
      header: t('billing.invoices.total'),
      cell: (row) => formatMoney(row.totalMinor, row.currency),
    },
    {
      id: 'status',
      header: t('billing.invoices.status'),
      cell: (row) => (
        <Badge variant={row.status === 'paid' ? 'success' : 'warning'}>
          {t(`billing.invoices.statuses.${row.status as 'paid'}`)}
        </Badge>
      ),
    },
  ];

  const filterLabel = (value: InvoiceFilter) => {
    if (value === 'all') return t('billing.invoices.filterAll');
    if (value === 'open') return t('billing.invoices.filterOpen');
    return t('billing.invoices.filterPaid');
  };

  return (
    <div className="space-y-6">
      <PageHeader title={t('billing.invoices.title')} description={t('billing.invoices.description')} />

      <div className="flex flex-wrap gap-2">
        {FILTERS.map((value) => (
          <Button
            key={value}
            type="button"
            size="sm"
            variant={status === value ? 'primary' : 'outline'}
            onClick={() => setStatus(value)}
          >
            {filterLabel(value)}
          </Button>
        ))}
      </div>

      <div className="relative" aria-busy={Boolean(openingDocumentId)}>
        <DataTable
          columns={columns}
          rows={invoices.data?.data ?? []}
          rowKey={(row) => row.id}
          loading={invoices.isLoading}
          onRowClick={(row) => void openInvoicePdf(row)}
          rowTooltip={() => t('billing.invoices.openPdf')}
          empty={t('billing.invoices.empty')}
        />
        {openingDocumentId ? (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-surface/70">
            <div className="flex items-center gap-2 text-body-sm text-fg-secondary">
              <Spinner className="size-5 text-brand" />
              <span>{t('billing.invoices.openingPdf')}</span>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};
